import { clampStickerWidth, hasMinimumVisibleStickerArea } from "./geometry";
import { AvailableSticker, PetHeaderSticker, SceneSize } from "./types";

export const MAX_REGULAR_STICKERS = 20;
export const regularStickerCount = (stickers: PetHeaderSticker[]) =>
  stickers.filter(
    (s) => !["plant", "firefly", "pot"].includes(s.asset.category)
  ).length;

export const addCatalogSticker = (
  stickers: PetHeaderSticker[],
  item: AvailableSticker
): PetHeaderSticker[] => {
  if (
    !item.asset.isActive ||
    stickers.some((s) => s.assetId === item.asset.key) ||
    regularStickerCount(stickers) >= MAX_REGULAR_STICKERS
  )
    return stickers;
  return [
    ...stickers,
    {
      id: `draft:${item.asset.key}`,
      assetId: item.asset.key,
      asset: item.asset,
      x: item.defaultX,
      y: item.defaultY,
      width: item.defaultWidth,
      depthSlot: item.defaultDepthSlot,
      stackOrder:
        Math.max(
          -1,
          ...stickers
            .filter((s) => s.depthSlot === item.defaultDepthSlot)
            .map((s) => s.stackOrder)
        ) + 1,
    },
  ];
};

// Only the edited object is raised; other depths retain their own ordering.
export const replaceEditedSticker = (
  stickers: PetHeaderSticker[],
  next: PetHeaderSticker
) =>
  stickers.map((sticker) =>
    sticker.id === next.id
      ? {
          ...next,
          stackOrder:
            Math.max(
              -1,
              ...stickers
                .filter(
                  (s) => s.id !== next.id && s.depthSlot === next.depthSlot
                )
                .map((s) => s.stackOrder)
            ) + 1,
        }
      : sticker
  );

export const constrainSticker = (
  sticker: PetHeaderSticker,
  defaultWidth: number,
  imageSize: SceneSize
): PetHeaderSticker => {
  const width = clampStickerWidth(sticker.width, defaultWidth);
  const height =
    (width * imageSize.width * sticker.asset.imageHeight) /
    (imageSize.height * sticker.asset.imageWidth);
  const safe = { ...sticker, width };
  const assetSize = {
    width: sticker.asset.imageWidth,
    height: sticker.asset.imageHeight,
  };
  if (hasMinimumVisibleStickerArea(safe, assetSize, imageSize)) return safe;

  // Project back towards a fully visible placement until at least 5% intersects.
  const home = {
    x: Math.max(0, Math.min(1 - width, safe.x)),
    y: Math.max(0, Math.min(1 - height, safe.y)),
  };
  let low = 0;
  let high = 1;
  for (let i = 0; i < 40; i += 1) {
    const fraction = (low + high) / 2;
    const candidate = {
      ...safe,
      x: home.x + (safe.x - home.x) * fraction,
      y: home.y + (safe.y - home.y) * fraction,
    };
    if (hasMinimumVisibleStickerArea(candidate, assetSize, imageSize))
      low = fraction;
    else high = fraction;
  }
  return {
    ...safe,
    x: home.x + (safe.x - home.x) * low,
    y: home.y + (safe.y - home.y) * low,
  };
};
